import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Configure axios to send cookies with every request (httpOnly auth)
axios.defaults.withCredentials = true;

// Auth header fallback for backwards compatibility during transition
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const authAPI = {
  register: (data) => axios.post(`${API}/auth/register`, data),
  login: (data) => axios.post(`${API}/auth/login`, data),
  logout: () => axios.post(`${API}/auth/logout`),
  me: () => axios.get(`${API}/auth/me`, { headers: getAuthHeader() }),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, new_password) => axios.post(`${API}/auth/reset-password`, { token, new_password }),
  changePassword: (current_password, new_password) => axios.post(`${API}/auth/change-password`, { current_password, new_password }, { headers: getAuthHeader() }),
};

export const clientsAPI = {
  getAll: () => axios.get(`${API}/clients`, { headers: getAuthHeader() }),
  getOne: (id) => axios.get(`${API}/clients/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/clients`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/clients/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/clients/${id}`, { headers: getAuthHeader() }),
  downloadTemplate: () => axios.get(`${API}/clients/bulk/template`, { headers: getAuthHeader(), responseType: 'blob' }),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/clients/bulk/upload`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
  },
  bulkConfirm: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/clients/bulk/confirm`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
  },
  bulkUpdate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/clients/bulk/update`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
  },
  getProjects: (clientId) => axios.get(`${API}/clients/${clientId}/projects`, { headers: getAuthHeader() }),
};

export const projectsAPI = {
  getAll: () => axios.get(`${API}/projects`, { headers: getAuthHeader() }),
  getOne: (id) => axios.get(`${API}/projects/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/projects`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/projects/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/projects/${id}`, { headers: getAuthHeader() }),
};

export const salesOrdersAPI = {
  getAll: () => axios.get(`${API}/sales-orders`, { headers: getAuthHeader() }),
  getOne: (id) => axios.get(`${API}/sales-orders/${id}`, { headers: getAuthHeader() }),
  getNextNumber: () => axios.get(`${API}/sales-orders/next-number`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/sales-orders`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/sales-orders/${id}`, data, { headers: getAuthHeader() }),
  updateStatus: (id, data) => axios.put(`${API}/sales-orders/${id}/status`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/sales-orders/${id}`, { headers: getAuthHeader() }),
  uploadPODocument: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/sales-orders/${id}/upload-po`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
  },
};

export const invoicesAPI = {
  getAll: () => axios.get(`${API}/invoices`, { headers: getAuthHeader() }),
  getOne: (id) => axios.get(`${API}/invoices/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/invoices`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/invoices/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/invoices/${id}`, { headers: getAuthHeader() }),
};

export const purchaseOrdersAPI = {
  getAll: () => axios.get(`${API}/purchase-orders`, { headers: getAuthHeader() }),
  getOne: (id) => axios.get(`${API}/purchase-orders/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/purchase-orders`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/purchase-orders/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/purchase-orders/${id}`, { headers: getAuthHeader() }),
};

export const usersAPI = {
  getAll: () => axios.get(`${API}/users`, { headers: getAuthHeader() }),
  getOne: (id) => axios.get(`${API}/users/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/auth/register`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/users/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/users/${id}`, { headers: getAuthHeader() }),
  register: (data) => axios.post(`${API}/auth/register`, data, { headers: getAuthHeader() }),
};

export const dashboardAPI = {
  getStats: () => axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() }),
};

export const retainersAPI = {
  getAll: () => axios.get(`${API}/retainers`, { headers: getAuthHeader() }),
  getOne: (id) => axios.get(`${API}/retainers/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/retainers`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/retainers/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/retainers/${id}`, { headers: getAuthHeader() }),
  updateMonth: (monthId, data) => axios.put(`${API}/retainers/months/${monthId}`, data, { headers: getAuthHeader() }),
  updateMonthStatus: (monthId, data) => axios.put(`${API}/retainers/months/${monthId}/status`, data, { headers: getAuthHeader() }),
  getDashboardStats: () => axios.get(`${API}/retainers/dashboard/stats`, { headers: getAuthHeader() }),
};

export const reportsAPI = {
  getInvoiceReports: () => axios.get(`${API}/reports/invoice`, { headers: getAuthHeader() }),
  getFilteredReports: (params) => axios.get(`${API}/reports/filtered`, { headers: getAuthHeader(), params }),
  getManagers: () => axios.get(`${API}/reports/managers`, { headers: getAuthHeader() }),
};

export const notificationsAPI = {
  getAll: () => axios.get(`${API}/notifications`, { headers: getAuthHeader() }),
  markAllRead: () => axios.post(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() }),
};

export const filesAPI = {
  downloadPO: (filename) => axios.get(`${API}/files/po_documents/${filename}`, { headers: getAuthHeader(), responseType: 'blob' }),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/files/upload`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
  },
};
